import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Avatar,
  Chip,
  Rating,
  Container,
  CircularProgress,
  Pagination,
  Stack,
  Paper,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupIcon from '@mui/icons-material/Group';
import WorkIcon from '@mui/icons-material/Work';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import EmailIcon from '@mui/icons-material/Email';
import { NavHashLink } from "react-router-hash-link";
import { useSelector } from "react-redux";
import { useAuthToken } from "../TokenContext";
import { useIntl } from "react-intl";

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s',
  maxWidth: '100%',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const FilterContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  margin: theme.spacing(1),
  minWidth: 180,
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    minWidth: 'unset',
    margin: theme.spacing(0.5),
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(2),
  padding: theme.spacing(1, 4),
}));

// Constants
const FILTER_OPTIONS = {
  timezone: [
    { value: 'indian_standard_time', label: 'Indian Standard Time' },
    { value: 'eastern_standard_time', label: 'Eastern Standard Time' },
    { value: 'central_standard_time', label: 'Central Standard Time' },
    { value: 'mountain_standard_time', label: 'Mountain Standard Time' },
    { value: 'pacific_standard_time', label: 'Pacific Standard Time' },
    { value: 'greenwich_mean_time', label: 'Greenwich Mean Time' },
    { value: 'central_european_time', label: 'Central European Time' },
    { value: 'western_european_time', label: 'Western European Time' },
    { value: 'australian_eastern_standard_time', label: 'Australian Eastern Standard Time' },
    { value: 'australian_central_standard_time', label: 'Australian Central Standard Time' },
    { value: 'australian_western_standard_time', label: 'Australian Western Standard Time' },
    { value: 'new_york_time', label: 'New York Time (Eastern Daylight Time)' },
    { value: 'los_angeles_time', label: 'Los Angeles Time (Pacific Daylight Time)' },
    { value: 'london_time', label: 'London Time (British Summer Time)' },
    { value: 'paris_time', label: 'Paris Time (Central European Summer Time)' },
    { value: 'tokyo_time', label: 'Tokyo Time (Japan Standard Time)' },
    { value: 'sydney_time', label: 'Sydney Time (Australian Eastern Daylight Time)' },
    { value: 'dubai_time', label: 'Dubai Time (Gulf Standard Time)' },
    { value: 'moscow_time', label: 'Moscow Time (Moscow Standard Time)' },
    { value: 'brazil_time', label: 'Brazil Time (Brasilia Time)' },
    { value: 'chicago_time', label: 'Chicago Time (Central Daylight Time)' },
    { value: 'houston_time', label: 'Houston Time (Central Daylight Time)' },
    { value: 'cape_town_time', label: 'Cape Town Time (South Africa Standard Time)' }
  ],
  podSize: ["2", "3", "5", "7"],
  podType: [
    { value: "Saas", label: "SaaS" },
    { value: "Web app", label: "Web App" },
    { value: "Java Developer", label: "Java Developer" }
  ],
  location: [
    { value: 'usa', label: 'USA' },
    { value: 'canada', label: 'Canada' },
    { value: 'india', label: 'India' },
    { value: 'australia', label: 'Australia' },
    { value: 'uk', label: 'United Kingdom' },
    { value: 'germany', label: 'Germany' },
    { value: 'france', label: 'France' },
    { value: 'china', label: 'China' },
    { value: 'japan', label: 'Japan' },
    { value: 'brazil', label: 'Brazil' },
    { value: 'south_africa', label: 'South Africa' },
    { value: 'mexico', label: 'Mexico' },
    { value: 'italy', label: 'Italy' },
    { value: 'spain', label: 'Spain' },
    { value: 'russia', label: 'Russia' },
    { value: 'south_korea', label: 'South Korea' },
    { value: 'new_zealand', label: 'New Zealand' },
    { value: 'singapore', label: 'Singapore' },
    { value: 'uae', label: 'United Arab Emirates' },
    { value: 'saudi_arabia', label: 'Saudi Arabia' },
    { value: 'argentina', label: 'Argentina' },
    { value: 'netherlands', label: 'Netherlands' },
    { value: 'sweden', label: 'Sweden' },
    { value: 'switzerland', label: 'Switzerland' },
    { value: 'norway', label: 'Norway' },
    { value: 'denmark', label: 'Denmark' },
    { value: 'finland', label: 'Finland' },
    { value: 'portugal', label: 'Portugal' },
    { value: 'poland', label: 'Poland' },
    { value: 'turkey', label: 'Turkey' },
    { value: 'indonesia', label: 'Indonesia' },
    { value: 'malaysia', label: 'Malaysia' },
    { value: 'thailand', label: 'Thailand' },
    { value: 'vietnam', label: 'Vietnam' },
    { value: 'philippines', label: 'Philippines' },
    { value: 'egypt', label: 'Egypt' },
    { value: 'nigeria', label: 'Nigeria' },
    { value: 'kenya', label: 'Kenya' },
    { value: 'ghana', label: 'Ghana' },
    { value: 'colombia', label: 'Colombia' },
    { value: 'chile', label: 'Chile' },
    { value: 'peru', label: 'Peru' },
    { value: 'venezuela', label: 'Venezuela' },
    { value: 'pakistan', label: 'Pakistan' },
    { value: 'bangladesh', label: 'Bangladesh' },
    { value: 'sri_lanka', label: 'Sri Lanka' },
    { value: 'nepal', label: 'Nepal' },
    { value: 'bhutan', label: 'Bhutan' },
    { value: 'maldives', label: 'Maldives' }
  ]
};

const FindPodsPartnerGuest = () => {
  const token = useAuthToken();
  const intl = useIntl();
  const { user } = useSelector((state) => state.auth);

  // State management
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [getData, setGetData] = useState([]);

  // Filter states
  const [filters, setFilters] = useState({
    timezone: '',
    podSize: '',
    podType: '',
    location: '',
  });

  // Fetch pods data
  const fetchPods = async () => {
    if (!token || !user?.data?.[0]) return;

    const body = {
      partner_code: user.data[0].partner_code,
      podlocation: filters.location || '',
      podtype: filters.podType || '',
      podSize: filters.podSize || '',
      timezone: filters.timezone || ''
    };

    try {
      const response = await fetch(
        process.env.REACT_APP_PARTNER_FINDPODINPARTNERGUEST,
        {
          method: 'POST',
          headers: {
            Authorization: token,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(body)
        }
      );
      const data = await response.json();
      setGetData(data.data || []);
    } catch (err) {
      console.error("Error fetching pods:", err);
      setGetData([]);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      setInitialLoading(true);
      await fetchPods();
      setInitialLoading(false);
    };

    if (token && user?.data?.[0]) {
      initializeData();
    }
  }, [token]);

  const handleFilterChange = (event) => {
    setFilters({
      ...filters,
      [event.target.name]: event.target.value,
    });
  };

  const applyFilters = async () => {
    setFilterLoading(true);
    await fetchPods();
    setFilterLoading(false);
    setCurrentPage(1); // Reset to first page after filtering
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = getData.slice(indexOfFirstItem, indexOfLastItem);

  if (initialLoading) {
    return (
      <Box
        sx={{
          marginTop: { xs: "4rem", sm: "4.5rem", md: "5rem" },
          marginRight: "16px",
          width: "100vw",
          maxWidth: "none",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh"
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        marginTop: { xs: "4rem", sm: "4.5rem", md: "5rem" },
        marginRight: "16px",
        transition: "margin 0.3s ease",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "none",
        overflow: "hidden"
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          py: { xs: 2, sm: 3, md: 4 },
          px: { xs: 1, sm: 2, md: 3 },
          boxShadow: theme => theme.palette.mode === 'dark' 
            ? 'none' 
            : '0 0 10px rgba(0,0,0,0.03)',
          backgroundColor: theme => theme.palette.mode === 'dark' 
            ? 'rgba(0,0,0,0.2)' 
            : 'rgba(255,255,255,0.7)',
          borderRadius: { xs: 0, sm: 1 },
          maxWidth: "100% !important",
          width: "100%"
        }}
      >
      <Typography variant="h4" align="center" gutterBottom>
        {intl.formatMessage({ id: "findBestPod" })}
      </Typography>

      <FilterContainer elevation={2} sx={{ width: "100%", boxSizing: "border-box" }}>
        <Grid container spacing={2} alignItems="center" sx={{ width: "100%" }}>
          <Grid item xs={12} sm={6} md={3}>
            <StyledFormControl fullWidth>
              <InputLabel>{intl.formatMessage({ id: "timeZone" })}</InputLabel>
              <Select
                name="timezone"
                value={filters.timezone}
                onChange={handleFilterChange}
                label="Time Zone"
              >
                <MenuItem value="">{intl.formatMessage({ id: "all" })}</MenuItem>
                {FILTER_OPTIONS.timezone.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </StyledFormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StyledFormControl fullWidth>
              <InputLabel>{intl.formatMessage({ id: "podSize" })}</InputLabel>
              <Select
                name="podSize"
                value={filters.podSize}
                onChange={handleFilterChange}
                label="Pod Size"
              >
                <MenuItem value="">{intl.formatMessage({ id: "all" })}</MenuItem>
                {FILTER_OPTIONS.podSize.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </StyledFormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StyledFormControl fullWidth>
              <InputLabel>{intl.formatMessage({ id: "podType" })}</InputLabel>
              <Select
                name="podType"
                value={filters.podType}
                onChange={handleFilterChange}
                label="Pod Type"
              >
                <MenuItem value="">{intl.formatMessage({ id: "all" })}</MenuItem>
                {FILTER_OPTIONS.podType.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </StyledFormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StyledFormControl fullWidth>
              <InputLabel>{intl.formatMessage({ id: "location" })}</InputLabel>
              <Select
                name="location"
                value={filters.location}
                onChange={handleFilterChange}
                label="Location"
              >
                <MenuItem value="">{intl.formatMessage({ id: "all" })}</MenuItem>
                {FILTER_OPTIONS.location.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </StyledFormControl>
          </Grid>

          <Grid item xs={12} display="flex" justifyContent="center">
            <StyledButton
              variant="contained"
              color="primary"
              onClick={applyFilters}
              startIcon={<WorkIcon />}
              disabled={filterLoading}
            >
              {filterLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                intl.formatMessage({ id: "applyFilters" })
              )}
            </StyledButton>
          </Grid>
        </Grid>
      </FilterContainer>

      {filterLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3} sx={{ width: "100%", margin: 0 }}>
            {currentItems.length > 0 ? (
              currentItems.map((pod, index) => (
                <Grid item xs={12} sm={6} md={4} key={pod.id || index}>
                  <StyledCard>
                    <CardContent sx={{ overflow: 'hidden', wordWrap: 'break-word' }}>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Avatar
                          src={
                            pod.picture 
                              ? `https://globldataprod.s3.amazonaws.com/${pod.picture}` 
                              : '/defaultimg.png'
                          }
                          sx={{ width: 56, height: 56, mr: 2 }}
                        />
                        <Box>
                          <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>
                            {pod.podName}
                          </Typography>
                          <Rating value={Number(pod.avgrating) || 0} readOnly precision={0.5} />
                        </Box>
                      </Box>

                      <Chip
                        label={
                          pod.partner_code === user.data[0].partner_code 
                            ? intl.formatMessage({ id: "internalPod" })
                            : intl.formatMessage({ id: "externalPod" })
                        }
                        color={pod.partner_code === user.data[0].partner_code ? "primary" : "error"}
                        sx={{ mb: 2 }}
                      />

                      <Divider sx={{ my: 2 }} />

                      <Stack spacing={1}>
                        <Box display="flex" alignItems="center">
                          <EmailIcon sx={{ mr: 1, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            {pod.email}
                          </Typography>
                        </Box>

                        <Box display="flex" alignItems="center">
                          <LocationOnIcon sx={{ mr: 1, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {pod.location}
                          </Typography>
                        </Box>

                        <Box display="flex" alignItems="center">
                          <GroupIcon sx={{ mr: 1, flexShrink: 0 }} />
                          <Typography variant="body2">Size: {pod.podsize}</Typography>
                        </Box>

                        <Box display="flex" alignItems="center">
                          <MonetizationOnIcon sx={{ mr: 1, flexShrink: 0 }} />
                          <Typography variant="body2">
                            Rate: {
                              user.data[0].manager_type !== "PartnerClient" 
                                ? pod.pototalrate 
                                : pod.pod_rate
                            }
                          </Typography>
                        </Box>

                        <Box display="flex" alignItems="center">
                          <WorkIcon sx={{ mr: 1, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {intl.formatMessage({ id: "podType" })}: {pod.podtype}
                          </Typography>
                        </Box>
                      </Stack>

                      <Button
                        component={NavHashLink}
                        to={`/FindPodDetailsClient/${pod.id}`}
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ mt: 3 }}
                      >
                        {intl.formatMessage({ id: "viewPodDetails" })}
                      </Button>
                    </CardContent>
                  </StyledCard>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography variant="h6" align="center">
                  {intl.formatMessage({ id: "noPods" })}
                </Typography>
              </Grid>
            )}
          </Grid>

          {getData.length > itemsPerPage && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={Math.ceil(getData.length / itemsPerPage)}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Container>
    </Box>
  );
};

export default FindPodsPartnerGuest;